import { useState } from 'react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { FillPdfDialog } from './FillPdfDialog';
import {
  GenerateDocumentDialog,
  type GenerateDocumentParams,
} from './GenerateDocumentDialog';
import { useDocumentTemplates } from './useDocumentTemplates';

export const downloadUrl = (url: string, fileName: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
};

type CreateDocumentButtonProps = {
  onGenerate: (params: GenerateDocumentParams) => void;
  isGenerating: boolean;
  onFillPdfForm: (templateId: string, templateName: string) => void;
  disabled?: boolean;
  disabledReason?: string;
};

export const CreateDocumentButton: React.FC<CreateDocumentButtonProps> = ({
  onGenerate,
  isGenerating,
  onFillPdfForm,
  disabled,
  disabledReason,
}) => {
  const { t } = useTranslation();
  const [pdfOpen, setPdfOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const templatesData = useDocumentTemplates();

  const trigger = (
    <Button variant="outline" size="sm" disabled={disabled || isGenerating}>
      <Icon name="Plus" size="auto" />
      {t('appointmentTools.documents.createDocument')}
      <Icon name="ChevronDown" size="auto" />
    </Button>
  );

  return (
    <>
      <DropdownMenu>
        {disabled && disabledReason ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <span>{trigger}</span>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{disabledReason}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        )}
        <DropdownMenuContent align="end" className="w-80">
          <DropdownMenuItem onSelect={() => setPdfOpen(true)}>
            <Icon name="FileText" size="lg" className="mr-2 text-text-indigo" />
            <Stack gap={0}>
              <Text inline>{t('appointmentTools.documents.fillPdfTitle')}</Text>
              <Text inline size="xs" color="tertiary">
                {t('appointmentTools.documents.fillPdfMenuHint')}
              </Text>
            </Stack>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDocOpen(true)}>
            <Icon
              name="WandSparkles"
              size="lg"
              className="mr-2 text-text-indigo"
            />
            <Stack gap={0}>
              <Text inline>
                {t('appointmentTools.documents.createDocOrLetter')}
              </Text>
              <Text inline size="xs" color="tertiary">
                {t('appointmentTools.documents.createDocOrLetterHint')}
              </Text>
            </Stack>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <FillPdfDialog
        open={pdfOpen}
        onOpenChange={setPdfOpen}
        isGenerating={isGenerating}
        onFillPdfForm={onFillPdfForm}
        templates={templatesData}
      />
      <GenerateDocumentDialog
        open={docOpen}
        onOpenChange={setDocOpen}
        isGenerating={isGenerating}
        onGenerate={onGenerate}
        templates={templatesData}
      />
    </>
  );
};
